/* eslint-disable no-nested-ternary */
import React, { FC, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, SideModal, Typography } from 'design_system/src';
import { getUser } from 'localStorage/authStorage';
import { findAllOzonios } from 'models/ozonio/controllers/ozonios.controller';
import { ReactComponent as Redirect } from 'design_system/src/static/icons/external-link.svg';
import { ReactComponent as Form } from 'design_system/src/static/icons/document-tick.svg';
import {
	fetchActiveConversation,
	updateConversationResponder,
	updateInfobipAgentId,
} from 'models/conversation/controllers/conversation.controller';
import {
	Conversation,
	ConversationResponderEnum,
	ConversationResponderValues,
} from 'models/conversation/interfaces/conversation.interface';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { ozonerContext } from '../OzonerContext';
import ResponderHistory from './ResponderHistory';

const INFOBIP_CONVERSATION_URL =
	'https://portal.infobip.com/conversations/all-work/preview?conversationId=';

const responderOptions = Object.values(ConversationResponderEnum).map(
	(value) => ({
		label: ConversationResponderValues[value],
		value,
	}),
);

interface InfobipConversationProps {
	open: boolean;
	setOpen: Function;
	conversation?: Conversation & { ozonerId?: string };
	financialFormId?: string;
	onUpdate?: (conversation: Conversation) => void;
}

const InfobipConversation: FC<InfobipConversationProps> = ({
	open,
	setOpen,
	conversation: initialConversation,
	financialFormId,
	onUpdate,
}) => {
	const { financialForm } = useContext(ozonerContext);
	const [conversation, setConversation] = useState<
		(Conversation & { ozonerId?: string }) | null
	>(initialConversation ?? null);
	const [loadingConversation, setLoadingConversation] = useState(false);
	const [loadingResponder, setLoadingResponder] = useState(false);
	const [agentOptions, setAgentOptions] = useState<
		{ value: string; label: string }[]
	>([]);

	useEffect(() => {
		const user = getUser();
		findAllOzonios(user?.company._id ?? '', '').then((ozons) => {
			setAgentOptions(
				ozons.data
					.filter((ozonio) => ozonio.isActive && ozonio.infobipId)
					.map((ozonio) => ({ value: ozonio.infobipId, label: ozonio.name }))
					.sort((a, b) => a.label.localeCompare(b.label)),
			);
		});
	}, []);

	useEffect(() => {
		if (initialConversation) {
			setConversation(initialConversation);
			return;
		}
		const formId = financialFormId ?? financialForm?.form?._id;
		if (open && formId) {
			setLoadingConversation(true);
			fetchActiveConversation(formId)
				.then(setConversation)
				.catch(() => setConversation(null))
				.finally(() => setLoadingConversation(false));
		}
	}, [open, financialFormId, financialForm?.form?._id, initialConversation]);

	const applyUpdate = (updated: Conversation) => {
		setConversation(updated);
		onUpdate?.(updated);
	};

	const handleChangeResponder = (e: any) => {
		if (!conversation?._id || !e.target.value) return;
		setLoadingResponder(true);
		updateConversationResponder(conversation._id, e.target.value)
			.then((updated) => {
				applyUpdate(updated);
				toasterNotification({
					msg: 'Solicitud Exitosa',
					style: 'light',
					toasterType: toasterOptions.success,
				});
			})
			.catch(commonErrorHandler)
			.finally(() => setLoadingResponder(false));
	};

	const handleChangeInfobipAgentId = (e: { target: { value: string } }) => {
		if (!conversation?._id || !e.target.value) return;
		const { value } = e.target;
		if (value === (conversation.infobipAgentId ?? '')) return;
		updateInfobipAgentId(conversation._id, value)
			.then((updated) => {
				applyUpdate(updated);
				toasterNotification({
					msg: 'Solicitud Exitosa',
					style: 'light',
					toasterType: toasterOptions.success,
				});
			})
			.catch(commonErrorHandler);
	};

	return (
		<SideModal open={open} setOpen={setOpen} size="normal">
			<div className="flex_content_col">
				<Typography
					scale="heading4"
					weight="600"
					textColor="primary_300"
					className="text_center flex_header p_b_md"
				>
					Conversación Infobip
				</Typography>
				<div className="flex_body p_x_md">
					{loadingConversation ? (
						<Typography scale="medium" weight="400">
							Cargando conversación...
						</Typography>
					) : conversation ? (
						<>
							<Typography scale="medium" weight="400" className="m_b_sm">
								Teléfono: {conversation.dialCode}
								{conversation.phone}
							</Typography>
							<a
								href={`${INFOBIP_CONVERSATION_URL}${conversation.conversationId}`}
								target="_blank"
								rel="noreferrer"
							>
								<Button
									scale="small"
									variant="outline"
									className="m_b_md"
									icon={<Redirect />}
								>
									Ver conversación en Infobip
								</Button>
							</a>
							{conversation.financialForm && conversation.ozonerId && (
								<Link
									to={`/ozoner/${conversation.ozonerId}?formId=${conversation.financialForm}&tab=2`}
								>
									<Button
										scale="small"
										variant="outline"
										className="m_b_md m_l_xs"
										icon={<Form />}
									>
										Ver solicitud
									</Button>
								</Link>
							)}
							<Typography scale="medium" weight="600" className="m_b_xs">
								¿Quién responde?
							</Typography>
							<Input
								title=""
								disabled={loadingResponder}
								name="responder"
								onChange={handleChangeResponder}
								type="select"
								options={responderOptions}
								value={conversation.responder}
								variant="default"
								className="w_100_per m_b_lg"
							/>
							<Typography scale="medium" weight="600" className="m_b_xs">
								Agent ID de Infobip
							</Typography>
							<Input
								title=""
								disabled={!!conversation.financialForm}
								name="infobipAgentId"
								type="select"
								options={agentOptions}
								value={conversation.infobipAgentId}
								onChange={handleChangeInfobipAgentId}
								variant="default"
								className="w_100_per m_b_lg"
							/>
							<ResponderHistory historic={conversation.responderHistoric} />
						</>
					) : (
						<Typography
							scale="medium"
							weight="400"
							className="m_b_md"
							textColor="neutral_700"
						>
							Sin conversación activa
						</Typography>
					)}
				</div>
			</div>
		</SideModal>
	);
};

export default InfobipConversation;
